import { EquipmentRecordStatus, ProgramType } from 'types';

import { api } from './api';

const manufacturerTag = 'EquipmentManufacturers' as const;
const brandTag = 'EquipmentBrands' as const;

/**
 * Frontend-facing shapes for the equipment manufacturer and brand admin routes, mirroring
 * `api/src/schemas/equipmentManufacturers.ts`. Declared locally, as the AVL admin service does,
 * so the portal does not couple to the server document types (`ObjectId`, `Date`).
 */
export interface EquipmentAlias {
  source: string;
  value: string;
}

export interface EquipmentHistoryChange {
  from: unknown;
  to: unknown;
}

export interface EquipmentHistoryEntry {
  changedBy: string;
  /** Enriched display name resolved server-side. */
  changedByName?: string;
  changedAt: string;
  changes: Record<string, EquipmentHistoryChange>;
  note?: string;
}

export interface EquipmentMeta {
  status?: string;
  createdAt?: string;
  modifiedAt?: string;
  createdBy?: string;
  modifiedBy?: string;
}

export interface EquipmentManufacturer {
  id: string;
  name: string;
  programTypes: ProgramType[];
  status: EquipmentRecordStatus;
  aliases: EquipmentAlias[];
  /** Active brands under this manufacturer; derived server-side, never stored. */
  activeBrandCount: number;
  history: EquipmentHistoryEntry[];
  createdByName?: string;
  modifiedByName?: string;
  meta?: EquipmentMeta;
}

export interface EquipmentBrand {
  id: string;
  manufacturerId: string;
  name: string;
  websiteUrl?: string;
  warrantyRegistrationUrl?: string;
  status: EquipmentRecordStatus;
  aliases: EquipmentAlias[];
  history: EquipmentHistoryEntry[];
  createdByName?: string;
  modifiedByName?: string;
  meta?: EquipmentMeta;
}

export interface EquipmentManufacturerListResponse {
  data: EquipmentManufacturer[];
  total: number;
}

export interface EquipmentBrandListResponse {
  data: EquipmentBrand[];
  total: number;
}

export interface ListManufacturersParams {
  search?: string;
  status?: EquipmentRecordStatus;
  programType?: ProgramType;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface ListBrandsParams {
  manufacturerId: string;
  search?: string;
  status?: EquipmentRecordStatus;
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface ManufacturerWritableFields {
  name: string;
  programTypes: ProgramType[];
  aliases: EquipmentAlias[];
}

export type CreateManufacturerRequest = ManufacturerWritableFields;

export interface UpdateManufacturerRequest {
  id: string;
  data: ManufacturerWritableFields & { note?: string };
}

/**
 * Create omits a URL the user left blank; update sends `null` for it. The POST schema types both
 * URLs as plain strings while the PUT schema types them `['string','null']`, because only an
 * update has a stored value to clear.
 */
export interface CreateBrandRequest {
  manufacturerId: string;
  name: string;
  aliases: EquipmentAlias[];
  websiteUrl?: string;
  warrantyRegistrationUrl?: string;
}

export interface UpdateBrandRequest {
  id: string;
  /** The brand's manufacturer before this save, so a re-map invalidates the side it left too. */
  previousManufacturerId: string;
  data: {
    manufacturerId: string;
    name: string;
    aliases: EquipmentAlias[];
    websiteUrl: string | null;
    warrantyRegistrationUrl: string | null;
    note?: string;
  };
}

export interface SetManufacturerStatusRequest {
  id: string;
  status: EquipmentRecordStatus;
  /** Activate only: inactive brands to bring back alongside the manufacturer. */
  brandIds?: string[];
  note?: string;
}

export interface SetManufacturerStatusResponse {
  manufacturer: EquipmentManufacturer;
  /** How many brands the call moved, cascading down on deactivate or up on activate. */
  brandsChanged: number;
}

export interface SetBrandStatusRequest {
  id: string;
  /** Carried for cache invalidation; the route reads the brand's own stored manufacturer. */
  manufacturerId: string;
  status: EquipmentRecordStatus;
  note?: string;
}

const listQueryString = (params: ListManufacturersParams | ListBrandsParams): string => {
  const searchParams = new URLSearchParams();
  if ('manufacturerId' in params && params.manufacturerId) {
    searchParams.set('manufacturerId', params.manufacturerId);
  }
  if (params.search) searchParams.set('search', params.search);
  if (params.status) searchParams.set('status', params.status);
  if ('programType' in params && params.programType) searchParams.set('programType', params.programType);
  if (params.limit !== undefined) searchParams.set('limit', String(params.limit));
  if (params.offset !== undefined) searchParams.set('offset', String(params.offset));
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortDir) searchParams.set('sortDir', params.sortDir);
  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

export const equipmentManufacturersApi = api.injectEndpoints({
  endpoints: (build) => ({
    getManufacturers: build.query<EquipmentManufacturerListResponse, ListManufacturersParams>({
      query: (params) => ({ url: `/v2/equipment-manufacturers${listQueryString(params)}` }),
      providesTags: (result) =>
        result
          ? [...result.data.map(({ id }) => ({ type: manufacturerTag, id })), { type: manufacturerTag, id: 'LIST' }]
          : [{ type: manufacturerTag, id: 'LIST' }],
    }),

    getManufacturer: build.query<EquipmentManufacturer, string>({
      query: (id) => ({ url: `/v2/equipment-manufacturers/${id}` }),
      providesTags: (_result, _error, id) => [{ type: manufacturerTag, id }],
    }),

    createManufacturer: build.mutation<EquipmentManufacturer, CreateManufacturerRequest>({
      query: (body) => ({ url: '/v2/equipment-manufacturers', method: 'POST', body }),
      invalidatesTags: [{ type: manufacturerTag, id: 'LIST' }],
    }),

    updateManufacturer: build.mutation<EquipmentManufacturer, UpdateManufacturerRequest>({
      query: ({ id, data }) => ({ url: `/v2/equipment-manufacturers/${id}`, method: 'PUT', body: data }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: manufacturerTag, id },
        { type: manufacturerTag, id: 'LIST' },
      ],
    }),

    /**
     * Both directions move brands too — deactivate cascades to every active brand, activate brings
     * back the named ones — so the brand list for this manufacturer is invalidated either way.
     */
    setManufacturerStatus: build.mutation<SetManufacturerStatusResponse, SetManufacturerStatusRequest>({
      query: ({ id, status, brandIds, note }) => ({
        url: `/v2/equipment-manufacturers/${id}/${status === EquipmentRecordStatus.active ? 'activate' : 'deactivate'}`,
        method: 'POST',
        body: {
          ...(status === EquipmentRecordStatus.active && brandIds?.length ? { brandIds } : {}),
          ...(note ? { note } : {}),
        },
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: manufacturerTag, id },
        { type: manufacturerTag, id: 'LIST' },
        { type: brandTag, id },
      ],
    }),

    getBrands: build.query<EquipmentBrandListResponse, ListBrandsParams>({
      query: (params) => ({ url: `/v2/equipment-brands${listQueryString(params)}` }),
      providesTags: (_result, _error, { manufacturerId }) => [{ type: brandTag, id: manufacturerId }],
    }),

    /**
     * Every brand write changes its manufacturer's `activeBrandCount`, which the list column and the
     * detail header both read, so the manufacturer tags are invalidated alongside the brand list.
     */
    createBrand: build.mutation<EquipmentBrand, CreateBrandRequest>({
      query: (body) => ({ url: '/v2/equipment-brands', method: 'POST', body }),
      invalidatesTags: (_result, _error, { manufacturerId }) => [
        { type: brandTag, id: manufacturerId },
        { type: manufacturerTag, id: manufacturerId },
        { type: manufacturerTag, id: 'LIST' },
      ],
    }),

    updateBrand: build.mutation<EquipmentBrand, UpdateBrandRequest>({
      query: ({ id, data }) => ({ url: `/v2/equipment-brands/${id}`, method: 'PUT', body: data }),
      invalidatesTags: (_result, _error, { data, previousManufacturerId }) => [
        { type: brandTag, id: data.manufacturerId },
        { type: brandTag, id: previousManufacturerId },
        { type: manufacturerTag, id: data.manufacturerId },
        { type: manufacturerTag, id: previousManufacturerId },
        { type: manufacturerTag, id: 'LIST' },
      ],
    }),

    setBrandStatus: build.mutation<EquipmentBrand, SetBrandStatusRequest>({
      query: ({ id, status, note }) => ({
        url: `/v2/equipment-brands/${id}/${status === EquipmentRecordStatus.active ? 'activate' : 'deactivate'}`,
        method: 'POST',
        body: note ? { note } : {},
      }),
      invalidatesTags: (_result, _error, { manufacturerId }) => [
        { type: brandTag, id: manufacturerId },
        { type: manufacturerTag, id: manufacturerId },
        { type: manufacturerTag, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetManufacturersQuery,
  useGetManufacturerQuery,
  useCreateManufacturerMutation,
  useUpdateManufacturerMutation,
  useSetManufacturerStatusMutation,
  useGetBrandsQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useSetBrandStatusMutation,
} = equipmentManufacturersApi;
